import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ScrollText,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Cpu,
  Image,
  ShoppingBag,
} from 'lucide-react';
import { authService } from '../services/auth';
import { useTheme, THEMES } from '../hooks/useTheme';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Дашборд', end: true },
  { to: '/admin/users', icon: Users, label: 'Пользователи', end: false },
  { to: '/admin/audit', icon: ScrollText, label: 'Аудит', end: false },
];

const siteItems = [
  { to: '/admin/ai', icon: Cpu, label: 'AI Lab', end: false },
  { to: '/admin/mockup', icon: Image, label: 'Mockup Lab', end: false },
  { to: '/admin/ozon', icon: ShoppingBag, label: 'Ozon Lab', end: false },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const user = authService.getUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-40 h-screen w-64
          bg-slate-900/95 backdrop-blur-sm border-r border-white/[0.06]
          flex flex-col
          transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/[0.06]">
          <NavLink to="/" className="flex items-center gap-2">
            <img src="/favicon/favicon.svg" className="w-6 h-6 object-contain" alt="Wite Lab" />
            <span className="text-lg font-semibold text-gradient bg-gradient-to-r from-slate-100 to-slate-300">
              Wite Lab
            </span>
          </NavLink>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 text-slate-400 hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                }`
              }
            >
              <item.icon className="w-4.5 h-4.5" />
              {item.label}
            </NavLink>
          ))}

          <div className="pt-3 mt-3 border-t border-white/[0.06]">
            <p className="px-3 pb-2 text-[10px] font-medium text-slate-600 uppercase tracking-wider">Сайты</p>
            {siteItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                    isActive
                      ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                  }`
                }
              >
                <item.icon className="w-4.5 h-4.5" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Theme switcher */}
        <div className="px-4 py-3 border-t border-white/[0.06]">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Тема</div>
          <div className="flex gap-1">
            {THEMES.map((t) => (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`flex-1 min-w-0 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
                  theme === t.id
                    ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                    : 'bg-slate-800/50 border-slate-700/30 text-slate-400 hover:text-slate-200 hover:border-slate-600/40'
                }`}
                title={t.label}
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {t.icon === 'moon' && <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />}
                  {t.icon === 'contrast' && (
                    <>
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 3v18" />
                      <path d="M12 3a9 9 0 0 1 0 18" fill="currentColor" stroke="none" />
                    </>
                  )}
                  {t.icon === 'sun' && (
                    <>
                      <circle cx="12" cy="12" r="4" />
                      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
                    </>
                  )}
                </svg>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* User / Logout */}
        <div className="p-3 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-sm font-bold">
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-200 truncate">{user?.username}</p>
              <p className="text-xs text-slate-500">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-slate-500 hover:text-red-400 transition"
              title="Выйти"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile) */}
        <header className="h-16 flex items-center px-4 lg:px-6 border-b border-white/[0.06] bg-slate-950/80 backdrop-blur-sm sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 text-slate-400 hover:text-slate-200"
          >
            <Menu className="w-5 h-5" />
          </button>
          <NavLink
            to="/"
            className="hidden lg:flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition"
          >
            <ChevronLeft className="w-4 h-4" />
            На главную
          </NavLink>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
